import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { arrayToMap } from "@entrilia/shared/utilities/arrayHelper";
import { Grid } from "@mui/material";
import adminApi from "../../../../api/adminApi";
import type { FundAccountingAppCode, IntegrationAppStatus } from "../../../../api/types/clientTypes";
import useClientContext from "../../../../context/useClientContext";
import BankOfCanadaLogo from "../../../../images/boc.png";
import BankOfEnglandLogo from "../../../../images/boe.png";
import BrexLogo from "../../../../images/brex.png";
import CascataLogo from "../../../../images/cascata.png";
import DanmarksNationalbankLogo from "../../../../images/dnb.png";
import EuropeanCentralBankLogo from "../../../../images/ecb.png";
import FloatRatesLogo from "../../../../images/floatrates.png";
import GpxLogo from "../../../../images/gpx.png";
import HmrcLogo from "../../../../images/hmrc.png";
import IntAppLogo from "../../../../images/intapp.png";
import JpmLogo from "../../../../images/jpm.png";
import MxLogo from "../../../../images/mx.png";
import PassthroughLogo from "../../../../images/passthrough.png";
import PlaidLogo from "../../../../images/plaid.png";
import QashqadeLogo from "../../../../images/qashqade.png";
import QuilttLogo from "../../../../images/quiltt.png";
import RampLogo from "../../../../images/ramp.png";
import SalesforceLogo from "../../../../images/salesforce.png";
import SnowflakeLogo from "../../../../images/snowflake.png";
import TwelveDataLogo from "../../../../images/twelvedata.png";
import YodleeLogo from "../../../../images/yodlee.png";
import { pageRoutes } from "../../../../routes";
import IntegrationsCard from "./IntegrationsCard";
import SaltEdgeIcon from "./SaltEdgeIcon";

const IntegrationsDashboard = () => {
  const { clientCode, hasAnyPermission, reportingPortalApp, salesforceApp, passthroughApp, dealCloudApp, gPxApp } =
    useClientContext();

  const [fundAccountingApps, fetchAccountingAppsError] = useFetch(adminApi.getFundAccountingAppsStatus);
  const [apiGatewaySubscriptions, fetchApiGatewaySubscriptionsError] = useFetch(adminApi.getApiGatewaySubscriptions);

  if (fetchAccountingAppsError) {
    logError(fetchAccountingAppsError, "[IntegrationsDashboard], getFundAccountingAppsStatus");
  }

  if (fetchApiGatewaySubscriptionsError) {
    logError(fetchApiGatewaySubscriptionsError, "[IntegrationsDashboard], getApiGatewaySubscriptions");
  }

  const fundAccountingAppsMap = arrayToMap(
    fundAccountingApps?.applications ?? [],
    (app) => app.appCode,
    (app) => app
  );

  const isQashqadeConfigured = apiGatewaySubscriptions?.subscriptions?.some(
    (subscription) => subscription.type === "Qashqade"
  );

  const isCascataConfigured = apiGatewaySubscriptions?.subscriptions?.some(
    (subscription) => subscription.type === "Cascata"
  );

  const hasAccessToClientIntegrations = hasAnyPermission(["ViewClientIntegrations", "ManageClientIntegrations"]);

  const getFundAccountingAppProps = (
    code: FundAccountingAppCode
  ): { appStatus: IntegrationAppStatus; configurationLink: string | undefined } => {
    const app = fundAccountingAppsMap.get(code);
    return {
      appStatus: app?.status ?? "NoStatus",
      configurationLink: app?.managementUrl,
    };
  };

  return (
    <Grid container spacing={3}>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--snowflake"
          title="Snowflake"
          text="A single, global platform that powers the Data Cloud. Snowflake is uniquely designed to connect businesses globally, across any type or scale of data."
          logoSrc={SnowflakeLogo}
          moreInfoLink="https://www.snowflake.com"
          appStatus={reportingPortalApp?.status ?? "NotConnected"}
          configurationRoute={
            !!reportingPortalApp && hasAccessToClientIntegrations
              ? `/${clientCode}/${pageRoutes.integration}/${pageRoutes.snowflake}`
              : undefined
          }
        />
      </Grid>
      {salesforceApp?.status && (
        <Grid>
          <IntegrationsCard
            cardId="integrations-card--salesforce"
            title="Salesforce"
            text="Salesforce is a robust CRM platform designed to help businesses manage and analyze customer interactions and data efficiently."
            logoSrc={SalesforceLogo}
            moreInfoLink="https://www.salesforce.com"
            appStatus={salesforceApp.status}
            configurationRoute={`/${clientCode}/${pageRoutes.integration}/${pageRoutes.salesforce}`}
          />
        </Grid>
      )}
      {dealCloudApp?.status && (
        <Grid>
          <IntegrationsCard
            cardId="integrations-card--dealcloud"
            title="DealCloud"
            text="Turn your metrics into informed business decisions."
            logoSrc={IntAppLogo}
            moreInfoLink="https://www.intapp.com/dealcloud"
            appStatus={dealCloudApp.status}
            configurationRoute={`/${clientCode}/${pageRoutes.integration}/${pageRoutes.dealcloud}`}
          />
        </Grid>
      )}
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--passthrough"
          title="Passthrough"
          text="Intelligent sub docs and KYC/AML for private markets."
          logoSrc={PassthroughLogo}
          moreInfoLink="https://passthrough.com"
          appStatus={passthroughApp?.status ?? "NotConnected"}
          configurationRoute={
            hasAccessToClientIntegrations
              ? `/${clientCode}/${pageRoutes.integration}/${pageRoutes.passthrough}`
              : undefined
          }
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--mx"
          title="MX"
          text="MX is a fast-growing fintech provider, partnering with financial institutions and banking providers to provide consolidated data."
          logoSrc={MxLogo}
          moreInfoLink="https://www.mx.com"
          {...getFundAccountingAppProps("MX")}
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--yodlee"
          title="Yodlee"
          text="Yodlee provides account aggregation services, consolidating information from multiple sources."
          logoSrc={YodleeLogo}
          moreInfoLink="https://www.yodlee.com"
          {...getFundAccountingAppProps("Yodlee")}
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--salt-edge"
          title="Salt Edge"
          text="Connect to thousands of banks, neobanks, and EMIs across the world with Salt Edge."
          Icon={SaltEdgeIcon}
          moreInfoLink="https://www.saltedge.com"
          {...getFundAccountingAppProps("Salt Edge")}
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--plaid"
          title="Plaid"
          text="Plaid enables seamless and secure access to financial data, allowing to link bank accounts, verify balances, and initiate transactions."
          logoSrc={PlaidLogo}
          moreInfoLink="https://plaid.com"
          {...getFundAccountingAppProps("Plaid")}
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--qashqade"
          title="qashqade"
          text="qashqade is an enterprise product suite designed to streamline the calculation, allocation, tracking and reporting processes for private markets."
          logoSrc={QashqadeLogo}
          moreInfoLink="https://www.qashqade.com/?utm_source=partner&utm_medium=website&utm_campaign=entrilia"
          appStatus={
            isQashqadeConfigured === undefined ? "NoStatus" : isQashqadeConfigured ? "Published" : "NotConfigured"
          }
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--cascata"
          title="Cascata"
          text="Automated distribution waterfall and management solutions for the private equity industry."
          logoSrc={CascataLogo}
          moreInfoLink="https://www.cascata-solutions.com"
          appStatus={
            isCascataConfigured === undefined ? "NoStatus" : isCascataConfigured ? "Published" : "NotConfigured"
          }
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--hmrc"
          title="HMRC"
          text="Submit your VAT returns directly to HMRC for seamless tax compliance."
          logoSrc={HmrcLogo}
          moreInfoLink="https://www.gov.uk/government/organisations/hm-revenue-customs"
          {...getFundAccountingAppProps("HMRC")}
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--twelve-data"
          title="Twelve Data"
          text="Access real-time and historical financial market data for better investment insights."
          logoSrc={TwelveDataLogo}
          moreInfoLink="https://twelvedata.com"
          {...getFundAccountingAppProps("TwelveData")}
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--float-rates"
          title="Float Rates"
          text="Get accurate and up-to-date foreign exchange rates for simplified currency conversions."
          logoSrc={FloatRatesLogo}
          moreInfoLink="https://www.floatrates.com"
          {...getFundAccountingAppProps("FloatRates")}
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--jpm"
          title="J.P. Morgan Access®"
          text="Global cash management platform for real-time account visibility, payments, and liquidity management across 50+ countries and 120+ currencies."
          logoSrc={JpmLogo}
          moreInfoLink="https://www.jpmorgan.com/payments/client-resource-center/channels/accessexperience"
          {...getFundAccountingAppProps("JPMorgan Chase")}
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--brex"
          title="Brex"
          text="AI-powered spend management platform combining business banking, corporate cards, bill pay, and expense automation in one system for high-growth companies."
          logoSrc={BrexLogo}
          moreInfoLink="https://www.brex.com"
          {...getFundAccountingAppProps("Brex")}
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--ramp"
          title="Ramp"
          text="Push your chart of accounts and dimensions to Ramp, and import coded card transactions, bills, reimbursements, and treasury activity for posting to the general ledger."
          logoSrc={RampLogo}
          moreInfoLink="https://ramp.com"
          {...getFundAccountingAppProps("Ramp")}
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--quiltt"
          title="Quiltt"
          text="Unified open banking API that connects financial accounts via multiple aggregators through a single integration, with built-in data normalization, and transaction enrichment."
          logoSrc={QuilttLogo}
          moreInfoLink="https://www.quiltt.io"
          {...getFundAccountingAppProps("Quiltt")}
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--gpx"
          title="GPX"
          text="A comprehensive carried interest solution for alternative investments."
          logoSrc={GpxLogo}
          moreInfoLink="https://www.gppowerx.com/what-we-do"
          appStatus={gPxApp?.status ?? "NotConnected"}
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--boe"
          title="Bank of England"
          text="Get reliable and timely GBP foreign exchange rates and SONIA benchmark indexes directly from the Bank of England for accurate currency conversions and interest rate calculations."
          logoSrc={BankOfEnglandLogo}
          moreInfoLink="https://www.bankofengland.co.uk"
          {...getFundAccountingAppProps("BankOfEngland")}
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--boc"
          title="Bank of Canada"
          text="Get timely official CAD exchange rates directly from the Bank of Canada for reliable financial calculations."
          logoSrc={BankOfCanadaLogo}
          moreInfoLink="https://www.bankofcanada.ca"
          {...getFundAccountingAppProps("BankOfCanada")}
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--dnb"
          title="Danmarks Nationalbank"
          text="Get timely official DKK exchange rates directly from Danmarks Nationalbank for reliable financial calculations."
          logoSrc={DanmarksNationalbankLogo}
          moreInfoLink="https://www.nationalbanken.dk/en"
          {...getFundAccountingAppProps("DanmarksNationalbank")}
        />
      </Grid>
      <Grid>
        <IntegrationsCard
          cardId="integrations-card--ecb"
          title="European Central Bank"
          text="Get official EUR foreign exchange reference rates directly from the European Central Bank for accurate currency conversions and reliable financial calculations."
          logoSrc={EuropeanCentralBankLogo}
          moreInfoLink="https://www.ecb.europa.eu/home/html/index.en.html"
          {...getFundAccountingAppProps("EuropeanCentralBank")}
        />
      </Grid>
    </Grid>
  );
};

export default IntegrationsDashboard;
